import {Component, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {JSONSchema7} from 'json-schema';
import {TreeNode} from 'primeng/api';
import {firstValueFrom} from 'rxjs';
import {ECpmAEPluginFunctionTypeApiEnum} from '../../../../gen/cpm-ae-api-ts/src/enums/ae-global/cpmae-plugins.api-enums';
import {
    CpmAEPluginFunctionApiSchema,
    CpmAEPluginSpecApiSchema
} from '../../../../gen/cpm-ae-api-ts/src/schemas/ae-global/cpmae-plugins.api-schemas';
import {ECpmCLocationTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-location.api-enums';
import {ECpmCTaskStatusApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {CpmCOrganizationApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-organization.api-model';
import {CpmCProjectApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-project.api-model';
import {CpmCServiceRequestGroupApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request-group.api-model';
import {CpmCServiceRequestApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request.api-model';
import {
    CpmCServiceRequestCreateApiSchema,
    CpmCServiceRequestUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-service-request.api-schemas';
import {CpmCTaskInputOutputMappingApiSchema} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';
import {CpmCLocationApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-location.api-service';
import {CpmCOrganizationApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-organization.api-service';
import {CpmCProjectApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-project.api-service';
import {CpmCServiceRequestGroupApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request-group.api-service';
import {CpmCServiceRequestApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request.api-service';
import {CpmCLocationTreeApiView} from '../../../../gen/cpm-c-api-ts/src/views/c-customer/cpmc-location-tree.api-view';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';
import {ActionJobsFunctionIoEditor} from '../../components/action-jobs-function-io-editor/action-jobs-function-io-editor.component';
import {ServiceRequestProgressDataCellDisplayComponent} from '../../data-cell-display/service-request-progress-data-cell-display/service-request-progress-data-cell-display.component';
import {ObjectStoreEntriesTransientCache} from '../../transient-cache/object-store-entries.transient-cache';
import {ActionJobsUtils} from '../../utils/action-jobs.utils';
import {CustomerUrlBuilderUtil} from '../../utils/customer-url-builder.util';
import {LocationUtils} from '../../utils/location.util';
import {ObjectStoreBindingUtils} from '../../utils/object-store-binding.utils';
import {ServiceRequestUtils} from '../../utils/service-request.utils';

export class NewEditServiceRequestModalConfig {
    constructor(
        public existing?: CpmCServiceRequestApiModel,
        public projectUUID?: string,
        public organizationUUID?: string
    ) {}
}

export class NewEditServiceRequestModalResult {
    constructor(public item?: CpmCServiceRequestApiModel) {}
}

/**
 * New/Edit ServiceRequest modal
 */
@Component({
    selector: 'app-new-edit-service-request-modal',
    templateUrl: './new-edit-service-request-modal.component.html',
    imports: [
        ReactiveFormsModule,
        PrimeNgImportsModule,
        SdkImportsModule,
        ActionJobsFunctionIoEditor,
        ServiceRequestProgressDataCellDisplayComponent
    ]
})
export class NewEditServiceRequestModalComponent extends IBaseCRUModalComponent<
    CpmCServiceRequestApiModel,
    NewEditServiceRequestModalComponent,
    NewEditServiceRequestModalConfig,
    NewEditServiceRequestModalResult
> {
    protected readonly ServiceRequestUtils = ServiceRequestUtils;
    protected readonly ECpmCTaskStatusApiEnum = ECpmCTaskStatusApiEnum;

    // ===============================================================
    // ===============================================================

    protected _item?: CpmCServiceRequestApiModel;
    protected _serviceRequestGroups: CpmCServiceRequestGroupApiModel[] = [];
    protected _projects: CpmCProjectApiModel[] = [];
    protected _organizations: CpmCOrganizationApiModel[] = [];
    protected _locationTreeRoot: TreeNode<CpmCLocationTreeApiView> = {children: []};

    // ===============================================================
    // ===============================================================

    protected _functionsTree: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema>[] = [];
    protected _selectedPlugin?: CpmAEPluginSpecApiSchema;
    protected _selectedPluginFunction?: CpmAEPluginFunctionApiSchema;
    protected _objectStoreEntriesTransientCache?: ObjectStoreEntriesTransientCache;
    private _objectStoreEntriesCacheGeneration = 0;
    protected _inputsMappings: Record<string, CpmCTaskInputOutputMappingApiSchema> = {};
    protected _inputsMappingValid = false;
    protected _warningShowed = false;
    protected _status?: ECpmCTaskStatusApiEnum;
    protected _isWaitingForInput?: boolean;

    private _lastOrganizationUuid?: string;

    @ViewChild(ActionJobsFunctionIoEditor)
    private readonly _ioEditor?: ActionJobsFunctionIoEditor;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.overrideMessagesKey = 'newEditServiceRequestModalMessages';

        this.goTo = () => {
            const customerUUID = this.globalsService.sessionService.getCustomer()?.uuid;
            this.globalsService.router.navigate([
                CustomerUrlBuilderUtil.customerServiceRequest(customerUUID, this._item?.uuid)
            ]);
        };

        this.configure(
            'ServiceRequestApiModel',
            (model: CpmCServiceRequestApiModel) => model.id ?? '<Unknown service request>'
        );
        this.canSubmitExtra = () => {
            if (this._selectedPlugin) return this._inputsMappingValid;

            return true;
        };
        this.config.width = this.isCreationMode ? '50rem' : '30rem';
        this.contentMinHeight = '28rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            service_request_group_uuid: new FormControl<string | null>(null, []),
            project_uuid: new FormControl<string | null>(null, []),
            organization_uuid: new FormControl<string | null>(null, []),
            location: new FormControl<TreeNode<CpmCLocationTreeApiView> | null>(null, []),
            description: new FormControl<string | null>(null, []),
            function: new FormControl<string | null>(null, [])
        });

        if (this.dialogOpenMode === EDialogOpenMode.NEW) {
            const modalConfig = this.config.data?.extended as NewEditServiceRequestModalConfig | undefined;

            if (modalConfig?.projectUUID) {
                this.form.controls['project_uuid'].setValue(modalConfig.projectUUID);
                this.form.controls['project_uuid'].disable();
            }

            if (modalConfig?.organizationUUID) {
                this.form.controls['organization_uuid'].setValue(modalConfig.organizationUUID);
                this.form.controls['organization_uuid'].disable();
            }
        }

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading service request groups...',
                f: async (): Promise<void> => {
                    this._serviceRequestGroups = (
                        await firstValueFrom(
                            CpmCServiceRequestGroupApiService.getServiceRequestGroupList(
                                {},
                                CpmCServiceRequestGroupApiService.dPaginatorConfig
                            )
                        )
                    ).data;
                }
            },
            {
                m: 'Loading projects...',
                f: async (): Promise<void> => {
                    this._projects = (
                        await firstValueFrom(
                            CpmCProjectApiService.getProjectList({}, CpmCProjectApiService.dPaginatorConfig)
                        )
                    ).data;
                }
            },
            {
                m: 'Loading organizations...',
                f: async (): Promise<void> => {
                    this._organizations = (
                        await firstValueFrom(
                            CpmCOrganizationApiService.getOrganizationList(
                                {},
                                CpmCOrganizationApiService.dPaginatorConfig
                            )
                        )
                    ).data;
                }
            },
            {
                m: 'Loading locations...',
                f: async (): Promise<void> => {
                    if (
                        this.dialogOpenMode === EDialogOpenMode.NEW ||
                        !this.config.data!.extended!.existing!.organization_uuid
                    )
                        return;

                    await this.fetchAndBuildLocationTree(this.config.data!.extended!.existing!.organization_uuid!);
                }
            },
            {
                m: 'Loading the service request...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this._item = await firstValueFrom(
                        CpmCServiceRequestApiService.getServiceRequestByUUID(
                            this.config.data?.extended?.existing?.uuid ?? ''
                        )
                    );

                    const result = ServiceRequestUtils.getStatus(this._item);

                    this._status = result.status;
                    this._isWaitingForInput = result.waitingForInput;
                }
            },
            {
                m: 'Loading service request generators...',
                f: async (): Promise<void> => {
                    this._functionsTree = await ActionJobsUtils.getFunctionsTree(
                        ECpmAEPluginFunctionTypeApiEnum.SERVICE_REQUEST_GENERATOR
                    );
                }
            },
            {
                m: 'Loading object store entries...',
                f: async () => {
                    if (!this.isCreationMode) return;
                    await this.rebuildObjectStoreEntriesCache();
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the service request...';

            const locationTreeNode = TreeUtils.findTreeNodeByKey(
                this._locationTreeRoot.children!,
                this._item!.location_uuid!
            );

            this.form.patchValue({
                ...this._item,
                location: locationTreeNode
            });
            this.form.updateValueAndValidity();
        }

        if (this.isCreationMode) {
            this.form.controls['function'].valueChanges.subscribe(this.onFunctionChanged.bind(this));

            this.form.controls['project_uuid'].valueChanges.subscribe(async () => {
                this.showLoading = true;
                await this.asyncCritical(async () => {
                    await this.rebuildObjectStoreEntriesCache();
                });
                this.showLoading = false;
            });
        }

        this.form.controls['organization_uuid'].valueChanges.subscribe(async () => {
            if (this.form.controls['organization_uuid']?.value == this._lastOrganizationUuid) {
                return;
            }

            this._lastOrganizationUuid = this.form.controls['organization_uuid']?.value;

            this.showLoading = true;
            this.form.controls['location']?.setValue(null);

            await this.asyncCritical(async () => {
                await this.fetchAndBuildLocationTree(this.form.controls['organization_uuid'].value);

                if (this.isCreationMode) await this.rebuildObjectStoreEntriesCache();
            });
            this.showLoading = false;
        });

        this.showChangeWarning();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCServiceRequestCreateApiSchema | CpmCServiceRequestUpdateApiSchema = this.isCreationMode
            ? ({
                  description: this.form.controls['description'].value,
                  service_request_group_uuid: this.form.controls['service_request_group_uuid'].value,
                  project_uuid: this.form.controls['project_uuid'].value,
                  organization_uuid: this.form.controls['organization_uuid'].value,
                  location_uuid: this.form.controls['location'].value?.key
              } as CpmCServiceRequestCreateApiSchema)
            : ({
                  description: this.form.controls['description'].value,
                  service_request_group_uuid: this.form.controls['service_request_group_uuid'].value,
                  project_uuid: this.form.controls['project_uuid'].value,
                  organization_uuid: this.form.controls['organization_uuid'].value,
                  location_uuid: this.form.controls['location'].value?.key
              } as CpmCServiceRequestUpdateApiSchema);

        if (this.isCreationMode && this._selectedPluginFunction) {
            const selectedFunction = this.form.controls['function'].value as TreeNode<
                CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema
            >;

            (toSubmit as CpmCServiceRequestCreateApiSchema).generator_plugin_id = selectedFunction.parent?.data?.id;
            (toSubmit as CpmCServiceRequestCreateApiSchema).generator_plugin_function_id = selectedFunction.data?.id;
            (toSubmit as CpmCServiceRequestCreateApiSchema).generator_inputs_mapping = this._inputsMappings;
        }

        await this.submitCU(
            null,
            CpmCServiceRequestApiService.createServiceRequest,
            CpmCServiceRequestApiService.updateServiceRequest,
            this._item?.uuid,
            toSubmit,
            (resultCRUD: CpmCServiceRequestApiModel) => new NewEditServiceRequestModalResult(resultCRUD)
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Fetch and build location tree
     * @param organizationUUID Parent organization UUID
     */
    private async fetchAndBuildLocationTree(organizationUUID: string) {
        this._locationTreeRoot.children?.splice(0, this._locationTreeRoot.children?.length);

        LocationUtils.buildLocationHierarchyTree(
            /* locations   */ (
                await firstValueFrom(
                    CpmCLocationApiService.getLocationTree(
                        {
                            downToType: ECpmCLocationTypeApiEnum.SITE
                        },
                        CpmCLocationApiService.dPaginatorConfig,
                        organizationUUID
                    )
                )
            ).data,
            /* rootNode     */ this._locationTreeRoot,
            /* autoExpand   */ [ECpmCLocationTypeApiEnum.REGION],
            /* selectable   */ [ECpmCLocationTypeApiEnum.SITE]
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * (Re)build the Object Store entries transient cache based on the currently selected
     * project/organization in the creation form, so that the Object Store bind choices
     * offered to the generator function inputs always match the scope of the Service
     * Request being created.
     */
    private async rebuildObjectStoreEntriesCache(): Promise<void> {
        const previousCache = this._objectStoreEntriesTransientCache;
        const generation = ++this._objectStoreEntriesCacheGeneration;

        const organizationUUID = this.form.controls['organization_uuid'].value ?? undefined;
        const projectUUID = this.form.controls['project_uuid'].value ?? undefined;

        const newCache = new ObjectStoreEntriesTransientCache(this.globalsService.sessionService.getCustomer()!.uuid, {
            organizationUUID,
            projectUUID
        });
        await newCache.init();

        if (generation !== this._objectStoreEntriesCacheGeneration) return;

        this._objectStoreEntriesTransientCache = newCache;

        const scopeChanged =
            !!previousCache &&
            (previousCache.organizationUUID !== organizationUUID || previousCache.projectUUID !== projectUUID);

        if (scopeChanged) {
            const expectedTypeByProperty = ObjectStoreBindingUtils.buildExpectedTypeByProperty(
                this._selectedPluginFunction?.input_schema as JSONSchema7 | undefined
            );

            const changed = ObjectStoreBindingUtils.invalidateStaleObjectStoreBindings(
                [this._inputsMappings],
                this._objectStoreEntriesTransientCache,
                expectedTypeByProperty
            );

            if (changed) {
                this._ioEditor?.notifyMappingChangedExternally();
                this.onObjectStoreBindingsInvalidated();
            }
        }
    }

    /**
     * Warn the user that one or more Object Store bindings configured for the generator
     * function inputs were dropped because the Project/Organization scope changed.
     */
    protected onObjectStoreBindingsInvalidated() {
        this.globalsService.messageService.add({
            key: this.overrideMessagesKey,
            severity: 'warn',
            detail: 'Changing the Project/Organization has reset one or more Object Store bindings configured for the generator function inputs.'
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the function is changed
     */
    protected onFunctionChanged() {
        if (this._selectedPluginFunction != this.form.controls['function'].value?.data) {
            // Reset state
            this._inputsMappings = {};
            this._inputsMappingValid = false;
        }

        // Change function
        this._selectedPlugin = this.form.controls['function'].value?.parent?.data as CpmAEPluginSpecApiSchema;
        this._selectedPluginFunction = this.form.controls['function'].value?.data;
    }
    /**
     * Show change warning
     */
    protected showChangeWarning() {
        if (this._warningShowed || this.dialogOpenMode !== EDialogOpenMode.EDIT) return;

        const message =
            'Changing Organization/Location may change variable allocation and/or function targets for this Service Request if Smart Tasks are used.';

        this.globalsService.messageService.add({
            key: this.overrideMessagesKey,
            severity: 'warn',
            detail: message
        });

        this._warningShowed = true;
    }
}
